import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Check, GraduationCap, Microscope, Compass, PenTool, Heart, Users } from 'lucide-react';
import personasData from '@/data/personas.json';
import { motion } from 'framer-motion';

interface PersonaSelectionProps {
    selectedPersonaId: string | null;
    onSelect: (personaId: string) => void;
}

const personaIcons: Record<string, any> = {
    'scholarly': GraduationCap,
    'devotional': Heart,
    'pastoral': Users,
    'seeker': Compass,
    'analytical': Microscope,
    'creative': PenTool
};

const personaColors: Record<string, string> = {
    'scholarly': 'text-blue-500 bg-blue-500/10 border-blue-500/20',
    'devotional': 'text-rose-500 bg-rose-500/10 border-rose-500/20',
    'pastoral': 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
    'seeker': 'text-amber-500 bg-amber-500/10 border-amber-500/20',
    'analytical': 'text-purple-500 bg-purple-500/10 border-purple-500/20',
    'creative': 'text-orange-500 bg-orange-500/10 border-orange-500/20'
};

export function PersonaSelection({ selectedPersonaId, onSelect }: PersonaSelectionProps) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {personasData.personas.map((persona, index) => {
                const isSelected = selectedPersonaId === persona.id;
                const Icon = personaIcons[persona.id] || User;
                const colorClass = personaColors[persona.id] || 'text-primary bg-primary/10 border-primary/20';

                return (
                    <motion.div
                        key={persona.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ y: -4, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <Card
                            className={`h-full cursor-pointer transition-all duration-300 relative backdrop-blur-md border-2 overflow-hidden ${
                                isSelected 
                                    ? 'border-primary ring-4 ring-primary/10 bg-primary/10' 
                                    : 'bg-background/40 border-border/40 hover:border-primary/30 hover:bg-background/60 shadow-lg'
                                }`}
                            onClick={() => onSelect(persona.id)}
                        >
                            <div className={`absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 rounded-full opacity-10 ${colorClass.split(' ')[1]}`} />
                            
                            {isSelected && (
                                <div className="absolute top-3 right-3 bg-primary text-primary-foreground rounded-full p-1 z-10 shadow-lg">
                                    <Check className="h-4 w-4" />
                                </div>
                            )}

                            <CardHeader className="pb-3 relative z-10">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 border ${colorClass}`}>
                                    <Icon className="h-6 w-6" />
                                </div>
                                <CardTitle className="text-xl font-bold tracking-tight">{persona.title}</CardTitle>
                                <CardDescription className="font-medium text-primary/70">
                                    {persona.targetAudience}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="relative z-10">
                                <p className="text-sm text-muted-foreground mb-4 leading-relaxed line-clamp-2">
                                    {persona.description}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {persona.alternateTitles.slice(0, 3).map((title, idx) => (
                                        <Badge key={idx} variant="outline" className="text-[10px] py-0 uppercase tracking-wider font-bold bg-background/50 border-border/30">
                                            {title}
                                        </Badge>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                );
            })}
        </div>
    );
}
